import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';

/**
 * Auto check-ins (②d N3): completing a pacted habit/routine/quest locally
 * publishes the pact check-in — no manual step. One writer, called from the
 * action stores' completion paths.
 *
 * Discipline:
 * - Zero network on the hot path for non-pact users: the active-pact lookup
 *   is cached 5 min per user (and invalidated by pact actions), so a habit
 *   completion with no matching pact costs nothing after the first fetch.
 * - Dedupe: daily pacts ride the DB UNIQUE(pact_id, user_id, check_in_date)
 *   via ignoreDuplicates; weekly pacts additionally skip when a check-in
 *   already exists this ISO week (Mon-Sun).
 * - NO social XP here — auto check-ins must not turn habit completion into
 *   an XP multiplier. The DB triggers still maintain pact streaks/progress
 *   and bond XP. Manual check-ins (custom pacts) keep their XP path.
 */

export type PactActionType = 'habit' | 'routine' | 'quest';

export interface ActivePactRef {
  id: string;
  cadence: string;
  creator_id: string;
  partner_id: string;
  /** P3 per-side refs (raw jsonb; may be absent pre-migration). */
  creator_ref?: unknown;
  partner_ref?: unknown;
  habit_id: string | null;
  routine_id: string | null;
  quest_id: string | null;
}

function refMatches(raw: unknown, action: { type: PactActionType; id: string }): boolean {
  if (!raw || typeof raw !== 'object') return false;
  const ref = raw as { type?: unknown; id?: unknown };
  return ref.type === action.type && ref.id === action.id;
}

/**
 * Pure: the active pacts THIS USER's action completion should check in.
 * P3: each side matches its OWN ref; the legacy single-entity columns
 * still match for the creator (pre-migration / unbackfilled rows).
 */
export function matchPactsForAction(
  pacts: ActivePactRef[],
  action: { type: PactActionType; id: string },
  userId: string
): ActivePactRef[] {
  return pacts.filter(p => {
    if (p.creator_id === userId) {
      if (p.creator_ref) return refMatches(p.creator_ref, action);
      return p[`${action.type}_id` as 'habit_id' | 'routine_id' | 'quest_id'] === action.id;
    }
    if (p.partner_id === userId) {
      return refMatches(p.partner_ref, action);
    }
    return false;
  });
}

/** Pure: Monday 00:00 of the ISO week containing `date` (local time). */
export function isoWeekStart(date: Date): Date {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = d.getDay(); // 0 = Sunday
  const diff = day === 0 ? 6 : day - 1;
  d.setDate(d.getDate() - diff);
  return d;
}

/** Pure: yyyy-mm-dd in local time (matches the DB's CURRENT_DATE intent). */
export function localDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const CACHE_TTL_MS = 5 * 60 * 1000;
let cache: { userId: string; fetchedAt: number; pacts: ActivePactRef[] } | null = null;

/** Pact create/accept/cancel changed the active set — refetch on next completion. */
export function invalidateActivePactsCache(): void {
  cache = null;
}

async function getActiveLinkedPacts(userId: string): Promise<ActivePactRef[]> {
  if (cache && cache.userId === userId && Date.now() - cache.fetchedAt < CACHE_TTL_MS) {
    return cache.pacts;
  }
  // select('*'): a named select of the unmigrated P3 ref columns would 500
  const { data, error } = await supabase
    .from('social_pacts')
    .select('*')
    .eq('status', 'active')
    .or(`creator_id.eq.${userId},partner_id.eq.${userId}`);
  if (error) return cache?.userId === userId ? cache.pacts : [];
  cache = { userId, fetchedAt: Date.now(), pacts: (data || []) as ActivePactRef[] };
  return cache.pacts;
}

/**
 * Fire-and-forget: publish check-ins for every active pact linked to this
 * just-completed action. Callers `void` it; failures stay silent (the manual
 * check-in path on the pacts hub remains the fallback).
 */
export async function autoCheckInPacts(action: { type: PactActionType; id: string }): Promise<void> {
  try {
    const { user } = await syncManager.safeAuthCheck();
    if (!user) return;

    const matches = matchPactsForAction(await getActiveLinkedPacts(user.id), action, user.id);
    if (matches.length === 0) return;

    const today = new Date();
    for (const pact of matches) {
      if (pact.cadence === 'weekly') {
        const { data: weekRows } = await supabase
          .from('social_pact_check_ins')
          .select('id')
          .eq('pact_id', pact.id)
          .eq('user_id', user.id)
          .gte('check_in_date', localDateString(isoWeekStart(today)))
          .limit(1);
        if ((weekRows || []).length > 0) continue;
      }

      await supabase
        .from('social_pact_check_ins')
        .upsert(
          { pact_id: pact.id, user_id: user.id, completed: true },
          { onConflict: 'pact_id,user_id,check_in_date', ignoreDuplicates: true }
        );
    }
  } catch (error) {
    console.error('Pact auto check-in failed:', error);
  }
}
